import {
  Button,
  FieldWrapper,
  Input,
  Label,
  SingleCombobox,
} from '@commenda/kakashi';
import { TStepGroup } from '@commenda/types';
import { useEffect } from 'react';
import { z } from 'zod';
import { useNotification } from '../../../hooks/useNotification';
import { useForm } from '../../../hooks/useZodForm';
import { useWorkflowTaskTemplate } from '../../context/WorkflowTaskTemplateContext';

const schema = z.object({
  name: z.string().trim().optional(),
  description: z.string().trim().optional(),
  legacyServiceRequestTaskGroupKey: z.string().trim().optional(),
});

type TUpdateGroupFormFields = z.infer<typeof schema>;

export const UpdateGroupForm = ({
  onGroupUpdate,
  onCancel,
  selectGroup,
}: {
  onGroupUpdate: (
    groupId: string,
    params: {
      name?: string;
      description?: string;
      legacyServiceRequestTaskGroupKey?: string;
    }
  ) => void;
  onCancel: () => void;
  selectGroup: TStepGroup;
}) => {
  const { setNotification } = useNotification();

  const { taskGroups: legacyServiceTaskGroups, isLoading } =
    useWorkflowTaskTemplate();
  const legacyServiceTaskGroupsOptions = legacyServiceTaskGroups?.map(
    (taskGroup) => ({
      label: taskGroup.taskGroupId,
      value: taskGroup.taskGroupId,
    })
  );

  const { register, handleSubmit, watch, setValue } =
    useForm<TUpdateGroupFormFields>({
      defaultValues: {
        name: selectGroup.name,
        description: selectGroup.description,
      },
    });

  const onSubmit = (data: TUpdateGroupFormFields) => {
    if (!data.name) {
      setNotification({
        title: 'Error',
        message: 'Group Name is required',
        show: true,
        valid: false,
      });
      return;
    }
    onGroupUpdate(selectGroup.id, {
      name: data.name,
      description: data.description,
      legacyServiceRequestTaskGroupKey:
        data.legacyServiceRequestTaskGroupKey || null,
    });
  };

  useEffect(() => {
    if (isLoading) return;
    setValue(
      'legacyServiceRequestTaskGroupKey',
      selectGroup.legacyServiceRequestTaskGroupKey
    );
  }, [isLoading, selectGroup, setValue]);

  return (
    <div className="flex flex-col gap-4 p-4 bg-bg-secondary rounded-md">
      <FieldWrapper>
        <Label>Group Name</Label>
        <Input placeholder="Group Name" {...register('name')} />
      </FieldWrapper>
      <FieldWrapper>
        <Label>Legacy Service Request Task Group</Label>
        <SingleCombobox
          options={legacyServiceTaskGroupsOptions}
          value={watch('legacyServiceRequestTaskGroupKey')}
          setValue={(value) =>
            setValue('legacyServiceRequestTaskGroupKey', value)
          }
        />
      </FieldWrapper>
      <FieldWrapper>
        <Label>Group Description</Label>
        <Input placeholder="Group Description" {...register('description')} />
      </FieldWrapper>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </div>
    </div>
  );
};
